import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import {
  getStudentsBySections,
  isOnlyISAWithSelections,
  isISAWithFullClassesSelections,
  getSelectedLength,
  isFullClassesSelections,
} from './index';

/**
 * Gets the value of the input of the sections dropdown within assign/manage modal.
 * This is called if ISA flag is turned OFF.
 * @param {object} - Data from AssignManageForm component.
 * @returns {object} - The value to be used in the input and a flag if all the sections are selected or not.
 * */
export const getInputValue = ({
  selectedAssigneesLength,
  allAssignees,
  allAvailableAssigneesLength,
  progressSections,
}) => {
  let value;
  let areAllSectionsSelected = false;

  if (selectedAssigneesLength === 0) {
    value = '';
  } else if (selectedAssigneesLength === 1) {
    value = allAssignees[0].name;

    if (progressSections.length) {
      areAllSectionsSelected = true;
    }
  } else if (selectedAssigneesLength !== allAvailableAssigneesLength) {
    value = 'Multiple Classes';
  } else {
    areAllSectionsSelected = true;
    value = progressSections.length ? 'Multiple Classes' : 'All Classes';
  }

  return {
    areAllSectionsSelected,
    value,
  };
};

/**
 * Gets the value of the input of the sections dropdown within assign/manage modal.
 * This is called if ISA flag is turned ON.
 * @param {object} - Data from AssignManageForm component.
 * @returns {object} - The value to be used in the input and a flag if all the sections are selected or not.
 * */
export const getInputValueByIndividualAssignment = ({
  selectedAssignment,
  studentsFormData,
  subjectClassList,
  selectedAssigneesLength,
  allAvailableAssigneesLength,
  allAssignees,
  progressSections,
  disabledStudents,
  classesStats = {},
}) => {
  let value;
  let areAllSectionsSelected = false;

  /* This is used for manage modal. */
  if (!!selectedAssignment) {
    const {
      id,
      assignees: assigneesList,
      section_name,
      section_id: assignmentSectionId,
    } = selectedAssignment;
    const assignmentId = id.toString();
    const { name, id: assigneeSectionId } =
      (assigneesList && assigneesList[0]) || {};
    const sectionId = (assigneeSectionId || assignmentSectionId).toString();
    const formSectionStudents =
      studentsFormData.filter(
        (student) =>
          student.sectionId === sectionId &&
          student.assignmentId === assignmentId &&
          !student.fromOtherISA,
      ) || [];
    const formSectionStudentsLength = formSectionStudents.length;
    const allStudents = getStudentsBySections(subjectClassList);
    const sectionStudentsLength = allStudents.filter(
      (student) => student.sectionId === sectionId,
    ).length;
    const sectionName = section_name || name;

    if (
      formSectionStudentsLength > 0 &&
      formSectionStudentsLength < sectionStudentsLength
    ) {
      const { firstName, lastName } = formSectionStudents[0];
      value =
        formSectionStudentsLength > 1
          ? `${sectionName} (${formSectionStudentsLength} Students)`
          : `${firstName} ${lastName}`;
    } else {
      value = `${sectionName} `;
    }
  } else {
    /* This is used for assign modal. */
    const areAllSelected =
      selectedAssigneesLength === allAvailableAssigneesLength;
    if (selectedAssigneesLength === 1 && areAllSelected) {
      areAllSectionsSelected = true;
    } else if (
      selectedAssigneesLength > 1 &&
      !progressSections.length &&
      areAllSelected
    ) {
      areAllSectionsSelected = true;
    } else {
      areAllSectionsSelected = areAllSelected;
    }

    if (
      selectedAssigneesLength === 1 &&
      (areAllSelected ||
        isOnlyISAWithSelections(allAssignees[0].id, classesStats))
    ) {
      value = allAssignees[0].name;
      if ((disabledStudents || []).length) {
        value += ' (Everyone Else)';
      }
    } else if (isISAWithFullClassesSelections(classesStats)) {
      const selectedStudentsLength = getSelectedLength({ ...classesStats });
      value = `${selectedStudentsLength} Students`;
    } else if (
      progressSections.length &&
      selectedAssigneesLength > 1 &&
      isFullClassesSelections(classesStats)
    ) {
      value = 'Multiple Classes';
    } else if (selectedAssigneesLength === 0 && studentsFormData.length === 0) {
      value = '';
    } else if (selectedAssigneesLength !== allAvailableAssigneesLength) {
      const totalStudents = [...studentsFormData];
      const groupStudentsBySectionId = groupBy(studentsFormData, 'sectionId');
      allAssignees.forEach(
        ({ sectionId }) => delete groupStudentsBySectionId[sectionId],
      );

      if (isEmpty(groupStudentsBySectionId) && selectedAssigneesLength > 1) {
        value = 'Multiple Classes';
      } else if (
        isEmpty(groupStudentsBySectionId) &&
        selectedAssigneesLength === 1
      ) {
        value = allAssignees[0].name;
      } else if (totalStudents.length === 1) {
        // show only student name
        const { firstName, lastName } = totalStudents[0];
        value = `${firstName} ${lastName}`;
      } else if (totalStudents.length === 0) {
        value = '';
      } else {
        value = `${totalStudents.length} Students`;
      }
    } else {
      value = 'All Classes';
    }
  }

  return {
    areAllSectionsSelected,
    value,
  };
};
